import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type ColligativeStrings,
  CUSTOM,
  colligativeMessages,
  DEFAULT_SIG,
  SolventFields,
  TEMPERATURE_OPTIONS,
  VantHoffFields,
} from "@/components/tools/chem-physical/colligative-shared";
import { FailureNotice } from "@/components/tools/chem-physical/heat-shared";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type ConcentrationMethod,
  type FixedPointSolution,
  type Property,
  solveFixedPoint,
} from "@/lib/tool-kit/chem-physical/colligative";
import {
  HEAT_MASS_UNITS,
  unitFactor,
  unitOptionsOf,
  unitSymbol,
} from "@/lib/tool-kit/chem-physical/heat";
import { temperatureSymbol } from "@/lib/tool-kit/chem-physical/units";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
} from "@/lib/tool-kit/chemistry/format";

const MASS_OPTIONS = unitOptionsOf(HEAT_MASS_UNITS);

function FixedPointAnswer({
  solution,
  texts,
  unit,
  method,
  sig,
  strings,
}: {
  solution: FixedPointSolution;
  texts: Record<string, string>;
  unit: { t: string; soluteMass: string; solventMass: string };
  method: ConcentrationMethod;
  sig: number;
  strings: ColligativeStrings;
}) {
  const fmt = useToolNumber();
  const p = (value: number) => formatPlain(fmt, value);
  const boil = solution.property === "boil";
  const degree = temperatureSymbol(unit.t);
  const answerText = `${formatSig(fmt, solution.solutionShown, sig)} ${degree}`;
  const label = boil
    ? strings.solutionBoilingPoint
    : strings.solutionFreezingPoint;
  const lines: string[] = [];
  if (
    method === "mass" &&
    solution.moles !== null &&
    solution.soluteGrams !== null &&
    solution.solventGrams !== null &&
    solution.molarMass !== null
  ) {
    if (unitFactor(HEAT_MASS_UNITS, unit.soluteMass) !== 1) {
      lines.push(
        t(strings.convertLine, {
          label: strings.soluteMass,
          value: texts.soluteMass.trim(),
          unit: unitSymbol(HEAT_MASS_UNITS, unit.soluteMass),
          converted: `${p(solution.soluteGrams)} g`,
        })
      );
    }
    if (unitFactor(HEAT_MASS_UNITS, unit.solventMass) !== 1) {
      lines.push(
        t(strings.convertLine, {
          label: strings.solventMass,
          value: texts.solventMass.trim(),
          unit: unitSymbol(HEAT_MASS_UNITS, unit.solventMass),
          converted: `${p(solution.solventGrams)} g`,
        })
      );
    }
    lines.push(
      t(strings.molesLine, {
        mass: p(solution.soluteGrams),
        molar: p(solution.molarMass),
        moles: p(solution.moles),
      }),
      t(strings.molalityLine, {
        moles: p(solution.moles),
        kg: p(solution.solventGrams / 1000),
        molality: p(solution.molality),
      })
    );
  }
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={label} value={answerText} />
        <StatCard
          label={boil ? strings.elevation : strings.depression}
          value={`${formatSig(fmt, solution.delta, sig)} °C`}
        />
        <StatCard
          label={strings.molality}
          value={`${formatSig(fmt, solution.molality, sig)} m`}
        />
      </StatGrid>
      {solution.i !== 1 ? <Notice>{strings.measuredNote}</Notice> : null}
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{boil ? strings.equationBoil : strings.equationFreeze}</p>
        {lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>
          {t(boil ? strings.shiftBoil : strings.shiftFreeze, {
            i: p(solution.i),
            k: p(solution.k),
            m: p(solution.molality),
            delta: formatSig(fmt, solution.delta, sig + 1),
          })}
        </p>
        <p>
          {t(boil ? strings.pointBoil : strings.pointFreeze, {
            pure: p(solution.pureC),
            delta: formatSig(fmt, solution.delta, sig + 1),
            result: `${formatSig(fmt, solution.solutionC, sig + 1)} °C`,
          })}
        </p>
        {unit.t === "C" ? null : (
          <p>
            {t(strings.answerLine, {
              result: `${formatSig(fmt, solution.solutionC, sig + 1)} °C`,
              answer: answerText,
            })}
          </p>
        )}
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, { label, value: answerText })}
      />
    </>
  );
}

export function FixedPointPanel({
  strings,
  sigChoice,
}: {
  strings: ColligativeStrings;
  sigChoice: string;
}) {
  const [property, setProperty] = useState<Property>("freeze");
  const [solvent, setSolvent] = useState("water");
  const [method, setMethod] = useState<ConcentrationMethod>("molality");
  const [choice, setChoice] = useState("nacl:ideal");
  const [text, setText] = useState({
    k: "",
    point: "",
    molality: "1.00",
    soluteMass: "",
    molarMass: "",
    solventMass: "",
    i: "2",
  });
  const [unit, setUnit] = useState({
    t: "C",
    soluteMass: "g",
    solventMass: "g",
  });
  const outcome = solveFixedPoint({ property, solvent, method, text, unit });
  const set = (patch: Partial<typeof text>) => setText({ ...text, ...patch });
  const typed =
    method === "molality"
      ? [text.molality]
      : [text.soluteMass, text.molarMass, text.solventMass];
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);
  const labels: Record<string, string> = {
    k: t(strings.customK, {
      constant: property === "boil" ? strings.kb : strings.kf,
    }),
    point:
      property === "boil"
        ? strings.pureBoilingPoint
        : strings.pureFreezingPoint,
    molality: strings.molality,
    soluteMass: strings.soluteMass,
    molarMass: strings.soluteMolarMass,
    solventMass: strings.solventMass,
    i: strings.vantHoff,
  };
  return (
    <>
      <PillGroup
        label={strings.propertyLabel}
        name="col-property"
        onChange={setProperty}
        options={[
          { value: "freeze", label: strings.propertyFreeze },
          { value: "boil", label: strings.propertyBoil },
        ]}
        value={property}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SolventFields
          idPrefix="col-fp"
          k={text.k}
          onK={(k) => set({ k })}
          onPoint={(point) => set({ point })}
          onSolvent={setSolvent}
          onTempUnit={(t2) => setUnit({ ...unit, t: t2 })}
          point={text.point}
          property={property}
          solvent={solvent}
          strings={strings}
          tempUnit={unit.t}
        />
        <VantHoffFields
          choice={choice}
          idPrefix="col-fp"
          onChoice={(next, factor) => {
            setChoice(next);
            if (factor !== null) {
              set({ i: factor });
            }
          }}
          onValue={(i) => {
            setChoice(CUSTOM);
            set({ i });
          }}
          strings={strings}
          value={text.i}
        />
      </div>
      <PillGroup
        label={strings.methodLabel}
        name="col-method"
        onChange={setMethod}
        options={[
          { value: "molality", label: strings.methodMolality },
          { value: "mass", label: strings.methodMass },
        ]}
        value={method}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {method === "molality" ? (
          <TextField
            hint={strings.molalityHint}
            id="col-fp-molality"
            label={strings.molality}
            numeric
            onChange={(molality) => set({ molality })}
            value={text.molality}
          />
        ) : (
          <>
            <QuantityInput
              id="col-fp-solute-mass"
              label={strings.soluteMass}
              onUnitChange={(value) => setUnit({ ...unit, soluteMass: value })}
              onValueChange={(soluteMass) => set({ soluteMass })}
              unit={unit.soluteMass}
              unitLabel={strings.unitMass}
              units={MASS_OPTIONS}
              value={text.soluteMass}
            />
            <TextField
              hint={strings.molarMassHint}
              id="col-fp-molar-mass"
              label={strings.soluteMolarMass}
              numeric
              onChange={(molarMass) => set({ molarMass })}
              value={text.molarMass}
            />
            <QuantityInput
              id="col-fp-solvent-mass"
              label={strings.solventMass}
              onUnitChange={(value) => setUnit({ ...unit, solventMass: value })}
              onValueChange={(solventMass) => set({ solventMass })}
              unit={unit.solventMass}
              unitLabel={strings.unitMass}
              units={MASS_OPTIONS}
              value={text.solventMass}
            />
          </>
        )}
        <SelectField
          id="col-fp-tunit"
          label={strings.resultsIn}
          onChange={(value) => setUnit({ ...unit, t: value })}
          options={TEMPERATURE_OPTIONS}
          value={unit.t}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <FixedPointAnswer
            method={method}
            sig={sig}
            solution={outcome}
            strings={strings}
            texts={text}
            unit={unit}
          />
        ) : (
          <FailureNotice
            failure={outcome}
            labels={labels}
            messages={colligativeMessages(strings)}
          />
        )}
      </section>
    </>
  );
}
